import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetupStore } from '@/stores/setupStore';
import { SETUP_PROGRESS_EVENT, type SetupProgressEventDetail } from '@/lib/setupEvents';
import { deriveSetupProgress, stepForPath } from '@/utils/setupProgress';
import type { SetupProgress, SetupStepId } from '@/types';

/**
 * Derive the setup progress view model. Read-only — mount
 * {@link useSetupRevalidation} exactly once (in the app shell) to keep the
 * underlying signals fresh.
 */
export function useSetupProgress(): SetupProgress {
  const signals = useSetupStore(s => s.signals);
  return useMemo(() => deriveSetupProgress(signals), [signals]);
}

/**
 * Keeps the setup signals in step with the backend. Mount once, in the app
 * shell. Three triggers, no polling:
 *
 *  - first mount, so the checklist never renders stale or hardcoded progress;
 *  - leaving a step's page (that's when the user just did the thing) and
 *    entering one, plus window focus / tab visibility for changes made
 *    elsewhere — the latter guarded by the store's staleness window;
 *  - a `setup:progress-changed` announcement from a mutation site, which flips
 *    the step immediately where that's provable and confirms with backoff.
 */
export function useSetupRevalidation(): void {
  const refresh = useSetupStore(s => s.refresh);
  const handleChange = useSetupStore(s => s.handleChange);
  const { pathname } = useLocation();
  const previousPath = useRef<string | null>(null);

  // Initial read of every step.
  useEffect(() => {
    void refresh(undefined, { force: true });
  }, [refresh]);

  // Route changes: revalidate the step we just left — that's when the user most
  // likely just satisfied it — and the one we just opened, so the page and the
  // bar can't disagree about the same step.
  useEffect(() => {
    const previous = previousPath.current;
    previousPath.current = pathname;
    if (previous === null || previous === pathname) return;

    const touched: SetupStepId[] = [];
    for (const path of [previous, pathname]) {
      const step = stepForPath(path);
      if (step && !touched.includes(step)) touched.push(step);
    }
    if (touched.length === 0) return;
    void refresh(touched, { force: true });
  }, [pathname, refresh]);

  // Focus / visibility: catches work finished in another tab or by the backend.
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === 'hidden') return;
      void refresh();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [refresh]);

  // In-app mutations announced by the feature that owns them.
  useEffect(() => {
    const onChange = (event: Event) => {
      const { step, optimistic } = (event as CustomEvent<SetupProgressEventDetail>).detail ?? {};
      if (!step) return;
      handleChange(step, optimistic);
    };
    window.addEventListener(SETUP_PROGRESS_EVENT, onChange);
    return () => window.removeEventListener(SETUP_PROGRESS_EVENT, onChange);
  }, [handleChange]);
}
